import { toast } from "react-toastify";

import {
  CopyAgenticLogicRuleRequestBodyType,
  CopyAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

/**
 * Fork a rule's current workspace into a rule of its own. The source travels
 * server-side — the body carries only the copy's name and description, so the
 * caller cannot save one rule's code under another rule's name.
 */
const copyAgenticLogicRule = async (
  payload: ApiRequest<CopyAgenticLogicRuleRequestBodyType, { ruleId: string }>
) => {
  const resp = await api.post<
    CopyAgenticLogicRuleResponseBodyType,
    CopyAgenticLogicRuleRequestBodyType
  >(`/v2/agentic-logic/rules/${payload.pathParams.ruleId}/copy`, payload.body);
  return resp.data;
};

export const useCopyAgenticLogicRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CopyAgenticLogicRuleResponseBodyType,
    Error,
    ApiRequest<CopyAgenticLogicRuleRequestBodyType, { ruleId: string }>,
    unknown
  >({
    mutationFn: async (data) => copyAgenticLogicRule(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rules(),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to copy rule${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
